import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    Button,
    Modal,
    Table,
    Input,
} from "reactstrap";
import { getSuppliersDropdownAction } from "../../redux/Action/Action";
import NewSupplier, { Supplier } from "../NewSupplier/NewSupplier";
import NewSalesRepLead, { SalesRepLead } from "../NewSalesRepLead/NewSalesRepLead";

import ModalComponent from "../../components/Modal/modal";
import "../TableView/TableView.scss";


interface Column<T> {
    key: keyof T;
    label: string;
    render?: (value: any, row: T) => React.ReactNode;
    sortable?: boolean;
}

interface TableViewProps<T extends Supplier | SalesRepLead> {
    readonly name: string;
    readonly Data: T[];
    readonly columns: ReadonlyArray<Column<T>>;
    readonly onDelete: (supplierId: string) => void;
    readonly supplierID?: string;
    readonly disabled?: boolean;
    readonly supplierName?: string;
}

function TableView<T extends Supplier | SalesRepLead>({
    name,
    Data: DataProp,
    columns,
    onDelete,
    supplierID,
    disabled = false,
    supplierName,
}: Readonly<TableViewProps<T>>) {
    const dispatch = useDispatch();
    const [modal, setModal] = useState(false);
    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState<{ key: keyof T; direction: "asc" | "desc" } | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAdd, setShowAdd] = useState(false);
    const [editItem, setEditItem] = useState<T | null>(null);
    const [isExitClick, setIsExitClick] = useState<boolean>(false);
    const [deleteItem, setDeleteItem] = useState<string | null>(null);
    const rowsPerPage = 5;



    useEffect(() => {
        dispatch(getSuppliersDropdownAction());
    }, [dispatch]);

    const filtereddata = React.useMemo(
        () =>
            DataProp?.filter((item) =>
                Object.values(item).some((value) =>
                    value?.toString().toLowerCase().includes(search.toLowerCase())
                )
            ) || [],
        [DataProp, search]
    );

    const totalPages = Math.ceil(filtereddata.length / rowsPerPage);

    const handleSort = (key: keyof T) => {
        setSortConfig((prev) => {
            if (prev && prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
    };

    const sorteddata = React.useMemo(() => {
        if (!sortConfig) return filtereddata;
        const sorted = [...filtereddata].sort((a, b) => {
            const aValue = a[sortConfig.key] || "";
            const bValue = b[sortConfig.key] || "";
            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [filtereddata, sortConfig]);

    const paginateddata = sorteddata.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );


    const cancelBtnHandler = () => {
        setIsExitClick(false);
        setDeleteItem(null);
    };

    const exitConfirmHandler = () => {
        if (deleteItem) {
            onDelete(deleteItem);
        }
        setIsExitClick(false);
        setDeleteItem(null);
    };
    const toggle = () => {
        setModal(!modal);
        setCurrentPage(1);
        setSortConfig(null);
        setShowAdd(false);
        setSearch("");
    };

    return (
        <div>
            <button
                type="button"
                className="Add-supplier-button"
                onClick={toggle}
                disabled={disabled}
                style={disabled ? { opacity: 0.5, pointerEvents: "none" } : undefined}
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    fill="currentColor"
                    className="bi bi-plus-lg"
                    viewBox="0 0 16 16"
                >
                    <path
                        fillRule="evenodd"
                        d="M8 2a.5.5 0 0 1 .5.5v5h5a.5.5 0 0 1 0 1h-5v5a.5.5 0 0 1-1 0v-5h-5a.5.5 0 0 1 0-1h5v-5A.5.5 0 0 1 8 2"
                    />
                </svg>
            </button>

            <Modal isOpen={modal} toggle={toggle} size="sm" className="custom-modal">
                <div className="p-4">
                    <div className="d-flex justify-content-between">
                        <h3 className="mb-4">{name}</h3>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={toggle}
                        ></button>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="position-relative w-50">
                            <i className="fa fa-search position-absolute top-50 start-0 translate-middle-y ps-3"></i>
                            <Input
                                type="text"
                                placeholder={`Search ${name}`}
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="ps-5"
                                style={{ height: "44px" }}
                            />
                            <div className="position-absolute top-50 end-0 translate-middle-y pe-3">
                                <button
                                    type="button"
                                    className="btn p-0"
                                    aria-label="Clear search"
                                    onClick={() => {
                                        setSearch(""); setCurrentPage(1);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            setSearch(""); setCurrentPage(1);
                                        }
                                    }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="AddAction-button"
                            onClick={() => {
                                setEditItem(null);
                                setShowAdd(true);
                            }}
                        >
                            <i className="fa-solid fa-circle-plus"></i>
                            {
                                name === "Suppliers"
                                    ? " Add new supplier"
                                    : "Add new sales rep lead"
                            }
                        </button>

                    </div>

                    {showAdd && (
                        name === "Suppliers" ? (
                            <NewSupplier
                                onCancel={() => {
                                    setShowAdd(false);
                                    setEditItem(null);
                                }}
                                supplier={editItem}
                            />
                        ) : (
                            <NewSalesRepLead
                                onCancel={() => {
                                    setShowAdd(false);
                                    setEditItem(null);
                                }}
                                salesRepLead={editItem}
                                newSupplierID={supplierID}
                                supplierName={supplierName}
                            />
                        )
                    )}

                    <div className="table-responsive">
                        <Table hover bordered>
                            <thead className="table-light">
                                <tr>
                                    {columns.map((col) => (
                                        <th
                                            key={String(col.key)}
                                            className="fixed-col-width"
                                            style={col.sortable ? { cursor: "pointer" } : undefined}
                                            onClick={col.sortable ? () => handleSort(col.key) : undefined}
                                        >
                                            <span className="supplier-header-flex">
                                                {col.label}
                                                {col.sortable && <i className="fa-solid fa-sort"></i>}
                                            </span>
                                        </th>
                                    ))}
                                    <th className="actions-col" >Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginateddata.length > 0 ? (
                                    paginateddata.map((item) => (
                                        <tr key={item.id}>
                                            {columns.map((col) => {
                                                let cellContent;
                                                if (col.render) {
                                                    cellContent = col.render(item[col.key], item);
                                                } else {
                                                    cellContent =
                                                        item[col.key] !== undefined && item[col.key] !== null
                                                            ? String(item[col.key])
                                                            : <span className="supplier-na">NA</span>;
                                                }
                                                return (
                                                    <td key={String(col.key)} className="fixed-col-width">
                                                        {cellContent}
                                                    </td>
                                                );
                                            })}
                                            <td className="actions-col">
                                                <div className="d-flex gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn p-0"
                                                        aria-label="Edit"
                                                        onClick={() => {
                                                            setEditItem(item);
                                                            setShowAdd(true);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                setEditItem(item);
                                                                setShowAdd(true);
                                                            }
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-pen-to-square text-primary"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn p-0"
                                                        aria-label="Delete"
                                                        onClick={() => {
                                                            setIsExitClick(true);
                                                            setDeleteItem(item.value);
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter" || e.key === " ") {
                                                                setIsExitClick(true);
                                                                setDeleteItem(item.value);
                                                            }
                                                        }}
                                                    >
                                                        <i className="fa-solid fa-trash text-danger"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={columns.length + 1} className="text-center">
                                            {
                                                name === "Suppliers"
                                                    ? "No suppliers found."
                                                    : "No sales rep leads found."
                                            }

                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                            color="secondary"
                            size="sm"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((prev) => prev - 1)}
                        >
                            Previous
                        </Button>
                        <span>
                            Page {currentPage} of {totalPages}
                        </span>
                        <Button
                            color="primary"
                            size="sm"
                            disabled={(currentPage === totalPages) || totalPages === 0}
                            onClick={() => setCurrentPage((prev) => prev + 1)}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            </Modal>
            {
                <ModalComponent
                    isOpen={isExitClick}
                    modalTitle={"Confirmation"}
                    toggle={cancelBtnHandler}
                    modalBody={
                        name === "Suppliers"
                            ? "Are you sure you want to delete this supplier?"
                            : "Are you sure you want to delete this sales rep lead?"
                    }
                    btnLeftHandler={exitConfirmHandler}
                    btnLeftLabel={"Yes"}
                    btnRightLabel=""
                />
            }
        </div>
    );
}

export default TableView;